import { useState, useEffect, useMemo } from "react";

// react-router components
import { Routes, Route, Navigate, useLocation } from "react-router-dom";

// @mui material components
import CssBaseline from "@mui/material/CssBaseline";

// Crypto Treenvest examples
import Sidenav from "layouts/Sidenav";
import Configurator from "layouts/Configurator";

// RTL plugins
import rtlPlugin from "stylis-plugin-rtl";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";

// Crypto Treenvest routes
import routes from "routes";

// Crypto Treenvest contexts
import { useMainController, setMiniSidenav } from "context";

// Images
import brandWhiteLong from "assets/images/treenvest1.png";
import brandWhiteSmall from "assets/images/treenvest.png";
import brandDarkLong from "assets/images/treenvest1.png";
import brandDarkSmall from "assets/images/treenvest.png";
import { useMoralis } from "react-moralis";
import { useUpdater } from "hooks/useUpdater";

const App = () => {
  const [
    { miniSidenav, direction, layout, sidenavColor, transparentSidenav, whiteSidenav, darkMode },
    dispatch,
  ] = useMainController();

  const { isWeb3Enabled, enableWeb3, isAuthenticated, isWeb3EnableLoading } = useMoralis();

  useEffect(() => {
    const connectorId = window.localStorage.getItem("connectorId");
    if (isAuthenticated && !isWeb3Enabled && !isWeb3EnableLoading)
      enableWeb3({ provider: connectorId });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, isWeb3Enabled]);

  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const [rtlCache, setRtlCache] = useState(null);
  const { pathname } = useLocation();

  // Cache for the rtl
  useMemo(() => {
    const cacheRtl = createCache({
      key: "rtl",
      stylisPlugins: [rtlPlugin],
    });

    setRtlCache(cacheRtl);
  }, []);

  // Open sidenav when mouse enter on mini sidenav
  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, false);
      setOnMouseEnter(true);
    }
  };

  // Close sidenav when mouse leave mini sidenav
  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true);
      setOnMouseEnter(false);
    }
  };

  // Setting the dir attribute for the body element
  useEffect(() => {
    document.body.setAttribute("dir", direction);
  }, [direction]);

  // Setting page scroll to 0 when changing the route
  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  const getRoutes = (allRoutes) =>
    allRoutes.map((route) => {
      if (route.collapse) {
        return getRoutes(route.collapse);
      }

      if (route.route) {
        return <Route exact path={route.route} element={route.component} key={route.key} />;
      }

      return null;
    });

  useUpdater();

  return direction === "rtl" ? (
    <CacheProvider value={rtlCache}>
      <CssBaseline />
      <Sidenav
        color={sidenavColor}
        brand={
          (miniSidenav && !darkMode) || whiteSidenav
            ? { long: brandDarkLong, small: brandDarkSmall }
            : { long: brandWhiteLong, small: brandWhiteSmall }
        }
        routes={routes}
        onMouseEnter={handleOnMouseEnter}
        onMouseLeave={handleOnMouseLeave}
      />
      {layout === "vr" && <Configurator />}
      <Routes>
        {getRoutes(routes)}
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
    </CacheProvider>
  ) : (
    <>
      <CssBaseline />
      <Sidenav
        brandName=""
        color={sidenavColor}
        brand={
          (transparentSidenav && !darkMode) || whiteSidenav
            ? { long: brandDarkLong, small: brandDarkSmall }
            : { long: brandWhiteLong, small: brandWhiteSmall }
        }
        routes={routes}
        onMouseEnter={handleOnMouseEnter}
        onMouseLeave={handleOnMouseLeave}
      />
      {layout === "vr" && <Configurator />}
      <Routes>
        {getRoutes(routes)}
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
    </>
  );
};

export default App;
